import { PauseIcon, PlayIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React, { useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';

import { getVoiceImageUrl } from '@/utils/voices';

export const SELECTED_VOICE_STORAGE_KEY = 'selectedVoice';
export const TTS_LANDING_CONTEXT_KEY = 'ttsLandingContext';

import Select from '../atoms/Select';
import Button from '../buttons/Button';

interface VoicesForLandingPageProps {
  voices: Voice[];
  language: string;
  showFilters?: boolean;
}

const VoicesForLandingPage: React.FC<VoicesForLandingPageProps> = ({
  voices,
  language: _language,
  showFilters = true,
}) => {
  const { t } = useLocale('text-to-speech');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedAccent, setSelectedAccent] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingVoice, setCurrentPlayingVoice] = useState('');
  const [open, setOpen] = useState(voices.length > 15 ? false : true);
  const [, setSelectedVoice] = useLocalStorage<{
    name: string;
    voice_id: string;
  } | null>(SELECTED_VOICE_STORAGE_KEY, null);

  const localVoices = useMemo(() => {
    const seenVoiceIds = new Set<string>();

    return voices.filter((voice) => {
      if (seenVoiceIds.has(voice.voice_id)) {
        return false;
      }

      seenVoiceIds.add(voice.voice_id);
      return true;
    });
  }, [voices]);

  const availableLanguages = useMemo(
    () =>
      Array.from(
        new Set(
          localVoices.flatMap((voice) =>
            Array.isArray(voice.language) ? voice.language : [voice.language],
          ),
        ),
      ).filter(Boolean) as string[],
    [localVoices],
  );

  const availableAccents = useMemo(() => {
    if (!selectedLanguage) {
      return [];
    }

    const languageVoices = localVoices.filter((voice) =>
      Array.isArray(voice.language)
        ? voice.language.includes(selectedLanguage)
        : voice.language === selectedLanguage,
    );

    return Array.from(
      new Set(languageVoices.map((voice) => voice.accent).filter(Boolean)),
    ) as string[];
  }, [localVoices, selectedLanguage]);

  const filteredVoices = useMemo(() => {
    let filtered = localVoices;

    if (selectedLanguage) {
      filtered = filtered.filter((voice) =>
        Array.isArray(voice.language)
          ? voice.language.includes(selectedLanguage)
          : voice.language === selectedLanguage,
      );
    }

    if (selectedAccent) {
      filtered = filtered.filter((voice) => voice.accent === selectedAccent);
    }

    return filtered;
  }, [localVoices, selectedAccent, selectedLanguage]);

  const playSample = (sampleUrl: string, voice: Voice) => {
    if (audioRef.current) {
      audioRef.current.src = sampleUrl;
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
      setCurrentPlayingVoice(voice.name);
      // Save the selected voice to localStorage for persistence across pages
      setSelectedVoice({
        name: voice.name,
        voice_id: voice.voice_id,
      });
    }
  };

  const pauseSample = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  return (
    <div id='voices-section' className='mx-auto'>
      <div className='mb-10 mt-10 text-center'>
        <p className='mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
          {t('Explore our variety of voices for different use cases')}
        </p>
      </div>
      {showFilters && (
        <div className='mb-6 flex justify-center space-x-4'>
          <Select
            options={[
              { value: '', label: t('Language') },
              ...availableLanguages
                .sort((a, b) => t(a).localeCompare(t(b)))
                .map((lang) => ({
                  value: lang,
                  label: t(lang),
                })),
            ]}
            label='Language'
            value={selectedLanguage}
            onChange={(value) => {
              setSelectedLanguage(value);
              setSelectedAccent('');
            }}
            className='w-48'
            placeholder={t('Language')}
          />
          <Select
            options={[
              { value: '', label: t('Accent') },
              ...availableAccents
                .sort((a, b) => t(a).localeCompare(t(b)))
                .map((accent) => ({
                  value: accent,
                  label: t(accent),
                })),
            ]}
            label='Accent'
            value={selectedAccent}
            onChange={(value) => setSelectedAccent(value)}
            className='w-48'
            disabled={!selectedLanguage}
            placeholder={t('Accent')}
          />
        </div>
      )}
      <div
        className='relative mx-auto grid h-fit w-full max-w-7xl grid-cols-1 gap-4 overflow-auto  overflow-x-hidden px-2 sm:p-6 md:grid-cols-3'
        style={{ maxHeight: '100%' }}
      >
        {filteredVoices.map((voice, presetIdx) => (
          <div
            key={voice.voice_id}
            className={clsxm(
              'group relative overflow-hidden flex cursor-pointer items-center justify-between rounded-2xl bg-white dark:bg-slate-800/80 p-5 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10 hover:-translate-y-1 hover:shadow-md hover:ring-primary-500/50 dark:hover:ring-primary-500/50 transition-all duration-300',
              presetIdx > 11 && !open ? 'hidden' : '',
            )}
          >
            <div>
              <p className='mb-1 text-left text-sm font-semibold leading-none text-gray-900 dark:text-slate-200'>
                {t(voice.name)}
                {/* {voice.new && (
                  <span className='ml-2 inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-primary-800'>
                    {t('New')}
                  </span>
                )} */}
                {voice.language && (
                  <span className='ml-2 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-blue-800'>
                    {Array.isArray(voice.language)
                      ? t(voice.language[0])
                      : t(voice.language)}
                    {voice.accent &&
                      voice.accent.toLowerCase() !==
                        (Array.isArray(voice.language)
                          ? voice.language[0].toLowerCase()
                          : voice.language.toLowerCase()) &&
                      ` (${t(voice.accent)})`}
                  </span>
                )}
                {voice.isOpenAI && (
                  <span className='ml-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium leading-4 text-green-800'>
                    {t('Saver')} - {t('10x less')}
                  </span>
                )}
              </p>
              <div className='flex items-center space-x-2'>
                <div className=''>
                  <Image
                    alt={t(voice.name)}
                    src={getVoiceImageUrl(voice.image)}
                    width='100'
                    height='100'
                    layout='fixed' // Added layout prop to fix the size of the image
                    className='w-16 rounded-md sm:w-20'
                    loading='lazy'
                  />
                </div>
                <div className=' w-1/2'>
                  <p className='text-left text-xs font-medium leading-none text-gray-500 dark:text-slate-400'>
                    {voice.description}
                  </p>
                </div>
              </div>
            </div>
            {voice.preview_url && (
              <div
                aria-label={
                  isPlaying && currentPlayingVoice === voice.name
                    ? t('Pause')
                    : t('Play')
                }
                className='flex h-fit w-fit cursor-pointer items-center justify-center'
                onClick={(e) => {
                  e.stopPropagation();
                  isPlaying && currentPlayingVoice === voice.name
                    ? pauseSample()
                    : playSample(voice.preview_url as string, voice);
                }}
              >
                {isPlaying && currentPlayingVoice === voice.name ? (
                  <PauseIcon
                    className='h-10 w-10 text-primary-500'
                    aria-hidden='true'
                  />
                ) : (
                  <PlayIcon
                    className='h-10 w-10 text-primary-500'
                    aria-hidden='true'
                  />
                )}
              </div>
            )}
          </div>
        ))}
        {!open && !selectedLanguage && (
          <div className='pointer-events-none absolute -inset-x-2.5 -bottom-2.5 h-[16.6%] bg-gradient-to-t from-white to-white/25 dark:from-slate-900 dark:to-slate-900/25 sm:h-[33.3%]'></div>
        )}
      </div>
      <audio ref={audioRef} onEnded={handleAudioEnd} />

      {filteredVoices.length > 15 && (
        <div className='pointer-events-none relative z-10 -mt-5 mb-10 flex justify-center'>
          <Button
            onClick={() => setOpen(!open)}
            className='pointer-events-auto inline-flex justify-center rounded-lg bg-primary-700 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-500'
          >
            <span>{open ? t('Hide') : t('Show more...')}</span>
          </Button>
        </div>
      )}
      <div className='mx-auto max-w-5xl text-center mt-12 mb-16 px-4 sm:px-6'>
        <div className='relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-slate-800 dark:via-slate-800/80 dark:to-slate-800 p-8 sm:p-16 shadow-xl ring-1 ring-gray-900/5 dark:ring-white/10'>
          {/* Background glows */}
          <div
            className='absolute -top-32 -right-32 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl'
            aria-hidden='true'
          />
          <div
            className='absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl'
            aria-hidden='true'
          />

          <div className='relative z-10'>
            <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 ring-1 ring-inset ring-primary-500/20 mb-8'>
              <svg
                className='w-8 h-8'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth='1.5'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'
                />
              </svg>
            </div>

            <h3 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-4'>
              {t('Discover More Voices')}
            </h3>

            <p className='mx-auto max-w-xl text-lg text-gray-600 dark:text-slate-300 mb-10'>
              {t(
                'Access 1,900+ premium voices across different languages and accents.',
              )}
            </p>

            <div className='flex flex-wrap justify-center gap-4 text-sm font-medium mb-12'>
              <span className='inline-flex items-center px-4 py-2 rounded-full bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300 ring-1 ring-inset ring-sky-700/10 dark:ring-sky-500/20'>
                <span className='mr-2 text-base'>🌍</span> {t('70+ Languages')}
              </span>
              <span className='inline-flex items-center px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 ring-1 ring-inset ring-emerald-700/10 dark:ring-emerald-500/20'>
                <span className='mr-2 text-base'>🎭</span>{' '}
                {t('Multiple Accents')}
              </span>
              <span className='inline-flex items-center px-4 py-2 rounded-full bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300 ring-1 ring-inset ring-purple-700/10 dark:ring-purple-500/20'>
                <span className='mr-2 text-base'>✨</span>{' '}
                {t('Premium Quality')}
              </span>
            </div>

            <div>
              <Link
                href='/tts'
                className='inline-flex items-center justify-center rounded-full bg-gray-900 px-8 py-4 text-sm font-semibold text-white shadow-lg hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl'
              >
                {t('Get Access to All Voices')}
                <svg
                  className='ml-2 -mr-1 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth='2'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3'
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoicesForLandingPage;
